import {Group} from '@visx/group';
import {Pie} from '@visx/shape';
import {Accessor} from '@visx/shape/lib/types';
import {isUndefined} from 'lodash';
import React, {FC, PropsWithChildren, ReactNode, useCallback, useEffect, useMemo, useState} from 'react';
import styled, {css} from 'styled-components';

import {HandlerOf} from '../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {PaletteColorsEnum} from '../../../../../../libs/shared/ui/src/styles/paletteModel';
import {useUserTheme} from '../../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {ChartProps} from '../../commons/types';
import {getOldPaletteColorScheme} from '../../helpers/colors';

/*
 * Constants.
 */

const LOADING_COLOR = PaletteColorsEnum.GREY;

/*
 * Props.
 */

export interface PieChartProps<T> extends Omit<ChartProps<T>, 'xAccessor' | 'color'> {
  /** Accessor for values */
  accessor: Accessor<T, number>;
  /** Sort values */
  sortValues?: ((a: number, b: number) => number) | null;
  /** Inner radius ratio (between 0 and 1, default to 0.75) */
  radiusRatio?: number;
  /** Label renderer */
  renderLabel?: (d: any, centroidX: number, centroidY: number, index: number, percent: number) => ReactNode;
  /** Callback when an arc is clicked */
  onArcClick?: HandlerOf<number>;
  /** Callback when an arc is hovered */
  onArcHover?: HandlerOf<number | undefined>;
  /** To set the hover state on an arc from outside the component */
  hoveredArcIndex?: number;
  /** Donut main color */
  color: PaletteColorsEnum;
}

/*
 * Style.
 */

const StyledPieChartContainer = styled.div`
  display: inline-block;
  position: relative;
`;

const StyledArc = styled.g<{$isLoading?: boolean; $isHovered: boolean}>`
  ${(p) =>
    !p.$isLoading &&
    p.$isHovered &&
    css`
      filter: url(#pieChartShadow);
    `}
`;

const StyledChildren = styled.div<{$left: number; $top: number}>`
  position: absolute;

  // Top and left set the center point of the donut, and then the
  // transform centers this div around that point
  top: ${(p) => `${p.$top}px`};
  left: ${(p) => `${p.$left}px`};
  transform: translate(-50%, -50%);

  display: flex;
  flex-direction: column;

  align-items: center;
  justify-content: center;
`;

/*
 * Component.
 */

// The children are positioned in the center of donut using the
// top and left values that define the center
const maybeRenderChildren = (children: ReactNode, top: number, left: number) => {
  if (!children) {
    return null;
  }

  return (
    <StyledChildren $top={top} $left={left}>
      {children}
    </StyledChildren>
  );
};

export const PieChart = <T,>({
  width,
  height,
  margin = {},
  color,
  accessor,
  sortValues = null,
  data,
  isLoading,
  radiusRatio = 0.75,
  renderLabel,
  onArcClick,
  onArcHover,
  hoveredArcIndex,
  children,
}: PropsWithChildren<PieChartProps<T>>): ReturnType<FC<PropsWithChildren<unknown>>> => {
  const theme = useUserTheme();
  const [stateHoveredArcIndex, setStateHoveredArcIndex] = useState<number | undefined>();

  useEffect(() => {
    setStateHoveredArcIndex(hoveredArcIndex);
  }, [hoveredArcIndex]);

  const innerWidth = width - (margin.left || 0) - (margin.right || 0);
  const innerHeight = height - (margin.top || 0) - (margin.bottom || 0);

  const radius = Math.min(innerWidth, innerHeight) / 2;

  const centerX = innerWidth / 2;
  const centerY = innerHeight / 2;

  const marginTop = margin.top || 12;
  const marginLeft = margin.left || 10;

  const top = centerY + marginTop;
  const left = centerX + marginLeft;

  const colorScheme = useMemo(
    () => getOldPaletteColorScheme(theme, isLoading ? LOADING_COLOR : color),
    [theme, color, isLoading],
  );

  const total = Object.values(data).reduce((a, b) => a + accessor(b), 0);

  const handleHoverEvent = useCallback(
    (index?: number) => {
      setStateHoveredArcIndex(index);
      if (onArcHover) {
        onArcHover(index);
      }
    },
    [onArcHover],
  );

  return (
    <StyledPieChartContainer>
      <svg width={width + marginTop * 2} height={height + marginLeft * 2}>
        <filter id="pieChartShadow">
          <feDropShadow dx="0" dy="0" stdDeviation="3.5" floodColor="black" floodOpacity="0.3" />
        </filter>
        <filter id="pieChartDilate">
          <feMorphology operator="dilate" radius="1.5" />
        </filter>
        <Group top={top} left={left}>
          <Pie
            data={[...data]}
            pieValue={accessor}
            pieSortValues={sortValues}
            outerRadius={radius}
            innerRadius={radiusRatio * radius}
          >
            {(pie) => {
              const arcs = pie.arcs.map((arc, index) => {
                const [centroidX, centroidY] = pie.path.centroid(arc);
                const hasSpaceForLabel = arc.endAngle - arc.startAngle >= 0.1;
                const arcPath = pie.path(arc);
                const arcFill = colorScheme[index % colorScheme.length];
                const arcFilter =
                  !isLoading && stateHoveredArcIndex === arc.index ? 'url(#pieChartDilate)' : undefined;

                return (
                  <StyledArc
                    // eslint-disable-next-line react/no-array-index-key
                    key={`arc-${index}`}
                    $isLoading={isLoading}
                    $isHovered={stateHoveredArcIndex === arc.index}
                    onClick={() => onArcClick && onArcClick(arc.index)}
                    onFocus={() => handleHoverEvent(arc.index)}
                    onMouseEnter={() => handleHoverEvent(arc.index)}
                    onMouseLeave={() => handleHoverEvent(undefined)}
                  >
                    <path d={arcPath ?? undefined} fill={arcFill} filter={arcFilter} />
                    {!isLoading &&
                      hasSpaceForLabel &&
                      renderLabel &&
                      renderLabel(
                        arc.data,
                        centroidX,
                        centroidY,
                        index,
                        Math.round((accessor(arc.data) * 100) / total),
                      )}
                  </StyledArc>
                );
              });

              // ordering of the arcs matters because it determines rendering order, which
              // determines z-index (there isn't an independent way to set z-index on svgs).
              // We need to render the hovered arc last, to make sure that when it expands it
              // is on top of the surrounding arcs
              if (!isUndefined(stateHoveredArcIndex)) {
                arcs.push(arcs.splice(stateHoveredArcIndex, 1)[0]);
              }

              return arcs;
            }}
          </Pie>
        </Group>
      </svg>
      {maybeRenderChildren(children, top, left)}
    </StyledPieChartContainer>
  );
};
